import { DEFAULT_EIGHTD_MAPPING, type EightDMapping } from "./stage-link";
import type { ProblemRecord } from "./types";

/**
 * Package #2.6 — realistic, ordered synthetic demo timelines.
 *
 * Stage due dates run in stage order, D-step due dates run in D order and each
 * D date stays on or before its linked stage (per the 8D mapping). Dates are
 * anchored to the demo "today" and each record's actual progress. Only due
 * dates (and, where unavoidable, the target close date) change — never a
 * status, evidence item or completed date.
 */

export const DEMO_TODAY = "2026-09-29";

/**
 * Deliberate overdue work: record code → { stage index: days overdue }.
 * Only unfinished stages are listed; their linked D step falls due with them.
 */
export const OVERDUE_PLAN: Record<string, Record<number, number>> = {
  "PR-2601": { 9: 12 },
  "PR-2602": { 11: 1 },
  "PR-2604": { 6: 18, 7: 4 },
  "PR-2611": { 11: 24 },
  "PR-2612": { 3: 9, 4: 3 },
};

const DAY = 86_400_000;
const toMs = (d: string) => Date.parse(d.slice(0, 10) + "T00:00:00Z");
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10) + "T00:00:00.000Z";

function hash(s: string) {
  let h = 7;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
}

export interface TimelineResult {
  stageDue: Record<string, string>;
  dDue: Record<string, string>;
  targetCloseAt: string;
  targetMoved: boolean;
}

export function orderedTimeline(
  record: Pick<ProblemRecord, "code" | "openedAt" | "targetCloseAt" | "stages" | "eightD">,
  today = DEMO_TODAY,
  plan = OVERDUE_PLAN,
  mapping: EightDMapping = DEFAULT_EIGHTD_MAPPING,
): TimelineResult {
  const now = toMs(today);
  const od = plan[record.code] ?? {};
  const stages = [...record.stages].sort((a, b) => a.index - b.index);
  const first = stages.findIndex((s) => s.status !== "complete");
  const due: number[] = new Array(stages.length).fill(0);

  if (first >= 0) {
    const s0 = stages[first]!;
    const tc = toMs(record.targetCloseAt);
    const slots = stages.length - 1 - first;
    const anchor =
      od[s0.index] !== undefined
        ? now - od[s0.index]! * DAY
        : Math.max(now, Math.min(now + (7 + (hash(record.code) % 15)) * DAY, tc - slots * 7 * DAY));
    const gapDays = slots ? Math.min(56, Math.max(7, Math.floor((tc - anchor) / DAY / slots))) : 14;
    due[first] = anchor;
    for (let i = first + 1; i < stages.length; i++) {
      const s = stages[i]!;
      const prev = due[i - 1]!;
      if (s.status === "in-progress" && od[s.index] !== undefined) {
        due[i] = Math.max(prev, now - od[s.index]! * DAY);
      } else if (s.status === "in-progress") {
        due[i] = Math.max(prev + gapDays * DAY, now);
      } else {
        due[i] = Math.max(prev + gapDays * DAY, now + 7 * DAY);
      }
    }
    // Completed stages: evenly spaced after opening, well before the current stage.
    if (first > 0) {
      const end = Math.min(anchor - 14 * DAY, now - 3 * DAY);
      const start = Math.min(toMs(record.openedAt) + 14 * DAY, end);
      for (let i = 0; i < first; i++) {
        due[i] = first === 1 ? end : Math.round(start + ((end - start) * i) / (first - 1));
      }
    }
  } else {
    const end = Math.min(toMs(record.targetCloseAt), now - 3 * DAY);
    const start = Math.min(toMs(record.openedAt) + 14 * DAY, end);
    stages.forEach(
      (_, i) => (due[i] = Math.round(start + ((end - start) * i) / (stages.length - 1))),
    );
  }
  // Round to whole days.
  for (let i = 0; i < due.length; i++) due[i] = toMs(toIso(due[i]!));

  const stageDue: Record<string, string> = {};
  stages.forEach((s, i) => (stageDue[s.id] = toIso(due[i]!)));
  const byIndex = new Map(stages.map((s, i) => [s.index, due[i]!]));

  // D steps: due with their linked stage (earlier D on a shared stage a week before).
  const steps = [...record.eightD].sort((a, b) => a.id.localeCompare(b.id));
  const dMs = steps.map((d) => {
    const link = mapping.links.find((l) => l.dSteps.includes(d.id));
    const sDue = (link && byIndex.get(link.stage)) ?? due[due.length - 1]!;
    const pos = link ? link.dSteps.indexOf(d.id) : 0;
    const offset = link ? (link.dSteps.length - 1 - pos) * 7 * DAY : 0;
    let v = sDue - offset;
    if (d.status === "complete") v = Math.min(v, now - 3 * DAY);
    return v;
  });
  // Keep D order without passing any linked stage date: backward min pass.
  for (let i = dMs.length - 2; i >= 0; i--) dMs[i] = Math.min(dMs[i]!, dMs[i + 1]!);
  const dDue: Record<string, string> = {};
  steps.forEach((d, i) => (dDue[d.id] = toIso(dMs[i]!)));

  const last = due[due.length - 1]!;
  const tcMs = toMs(record.targetCloseAt);
  const targetMoved = last > tcMs;
  return {
    stageDue,
    dDue,
    targetCloseAt: targetMoved ? toIso(last) : record.targetCloseAt,
    targetMoved,
  };
}

export function applyTimeline<
  R extends Pick<ProblemRecord, "code" | "openedAt" | "targetCloseAt" | "stages" | "eightD">,
>(record: R, today = DEMO_TODAY): R {
  const t = orderedTimeline(record, today);
  return {
    ...record,
    targetCloseAt: t.targetCloseAt,
    stages: record.stages.map((s) => ({ ...s, dueDate: t.stageDue[s.id] ?? s.dueDate })),
    eightD: record.eightD.map((d) => ({ ...d, dueDate: t.dDue[d.id] ?? d.dueDate })),
  };
}

/** Overdue unfinished stages and D steps on a given day. */
export function overdueItems(record: Pick<ProblemRecord, "stages" | "eightD">, today = DEMO_TODAY) {
  const late = (due: string) => Math.round((toMs(today) - toMs(due)) / DAY);
  return [
    ...record.stages
      .filter((s) => s.status !== "complete" && late(s.dueDate) > 0)
      .map((s) => ({ id: `Stage ${s.index}`, days: late(s.dueDate) })),
    ...record.eightD
      .filter((d) => d.status !== "complete" && late(d.dueDate) > 0)
      .map((d) => ({ id: d.id, days: late(d.dueDate) })),
  ];
}
